import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { reportError, type ErrorReport } from '@/lib/error-reporting'

/**
 * Stops a render crash from becoming a white screen.
 *
 * Without this, one bad value in one component takes down the entire app and
 * the user is left staring at nothing — with no idea whether their data
 * survived. It did: everything is in IndexedDB, untouched by a render error.
 * The recovery copy says so, because that is the first thing anyone wants to
 * know.
 *
 * Two boundaries are used (see App.tsx): one around the whole app as a last
 * resort, and one around the view area so a broken view can be recovered from
 * by navigating away, without losing the shell.
 */

interface Props {
  children: ReactNode
  /** Named in the report so we know which part failed. */
  context: string
  /** Reset when this changes — lets navigation clear a broken view. */
  resetKey?: string
  /** `view` keeps the app chrome; `app` is the full-page fallback. */
  variant?: 'app' | 'view'
}

interface State {
  error: Error | null
  report: ErrorReport | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, report: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const report = reportError(error, { context: this.props.context, source: 'boundary' })
    // React's component stack is more useful than the JS stack here, and it
    // contains only component names — no user data.
    if (report && info.componentStack) {
      report.frames = info.componentStack
        .split('\n')
        .map((line) => line.trim().replace(/^at\s+/, '').split(' ')[0])
        .filter(Boolean)
        .slice(0, 8)
    }
    this.setState({ report })
  }

  componentDidUpdate(prev: Props) {
    // Navigating away from a broken view should clear it.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, report: null })
    }
  }

  private retry = () => this.setState({ error: null, report: null })

  render() {
    const { error, report } = this.state
    if (!error) return this.props.children

    const isApp = this.props.variant !== 'view'

    return (
      <div
        className={
          isApp
            ? 'min-h-screen flex items-center justify-center bg-background p-6'
            : 'flex items-center justify-center py-16 px-4'
        }
        role="alert"
      >
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-amber-500" />
          <h2 className="mb-2 text-lg font-semibold">Something went wrong here</h2>
          <p className="mb-1 text-sm text-muted-foreground">
            {isApp ? 'TrackMind hit an unexpected error.' : 'This view failed to load.'}
          </p>
          {/* The single most reassuring fact, and it happens to be true. */}
          <p className="mb-4 text-sm text-muted-foreground">
            <strong className="text-foreground">Your data is safe.</strong> It's stored on this
            device and nothing here has changed it.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button size="sm" onClick={this.retry}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Try again
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Reload
            </Button>
          </div>

          {report && (
            <details className="mt-6 text-left">
              <summary className="cursor-pointer text-xs text-muted-foreground">
                Technical details
              </summary>
              {/* Deliberately the scrubbed report, not the raw error: this is
                  the text people paste into a bug report or a public issue. */}
              <pre className="mt-2 overflow-x-auto rounded border bg-muted/40 p-3 text-left text-[11px] leading-relaxed">
{`${report.name}: ${report.message}
where: ${report.context}
version: ${report.appVersion}
${report.frames.slice(0, 5).join('\n')}`}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
